import type { BodyParts, Part } from '@chiroup/core/types/Survey.type';
import { partsOrder } from './bodyData';

const SIDES = {
  left: 'left',
  right: 'right',
} as const;

export const createName = (parts: BodyParts): string => {
  let partsInvolved = Object.values(parts || {}).reduce((arr: string[], sides) => {
    Object.entries(sides).forEach(([partName, partObj]) => {
      const { left, right } = partObj;
      let bodyPart = partName.replace(/-/g, ' ');

      if (left && right) {
        bodyPart = pluralizeBodyPart(bodyPart as Part);

        if (!arr.includes(bodyPart)) {
          arr.push(bodyPart);
        }
      } else if (left || right) {
        bodyPart = left ? `${SIDES.left} ${bodyPart}` : `${SIDES.right} ${bodyPart}`;

        if (!arr.includes(bodyPart)) {
          arr.push(bodyPart);
        }
      }
    });

    return arr;
  }, []);

  const backParts = partsInvolved.filter(part => part.includes('back'));

  if (backParts?.length > 1) {
    partsInvolved = partsInvolved.filter(part => !part.includes('back'));
    partsInvolved.push('back');
  }

  return partsInvolved
    .sort((a, b) => {
      const complaintNameA = conformNameToPartsOrder(a);
      const complaintNameB = conformNameToPartsOrder(b);

      const aOrder = complaintNameA in partsOrder ? partsOrder[complaintNameA] : 20;
      const bOrder = complaintNameB in partsOrder ? partsOrder[complaintNameB] : 20;

      if (aOrder < bOrder) {
        return -1;
      }

      if (aOrder > bOrder) {
        return 1;
      }

      return 0;
    })
    .reduce(
      (bodyPartsString, currentBodyPart, i) => {
        if (bodyPartsString.includes(SIDES.left)) {
          currentBodyPart = currentBodyPart.replace(`${SIDES.left} `, '');
        } else if (bodyPartsString.includes(SIDES.right)) {
          currentBodyPart = currentBodyPart.replace(`${SIDES.right} `, '');
        }

        return `${bodyPartsString}${
          i ? (partsInvolved.length === i + 1 ? ' and ' : ', ') : ''
        }${currentBodyPart}`;
      },

      ''
    );
};

function pluralizeBodyPart(bodypart: Part): string {
  switch (bodypart) {
    case 'ankle':
    case 'arm':
    case 'elbow':
    case 'forearm':
    case 'hand':
    case 'hip':
    case 'knee':
    case 'leg':
    case 'rib':
    case 'shoulder':
    case 'thigh':
    case 'wrist':
      return `${bodypart}s`;
    case 'foot':
      return 'feet';
    case 'abdomen':
    case 'chest':
    case 'groin':
    case 'head':
    case 'lower-back':
    case 'neck':
    case 'upper-back':
    case 'upper-lower-back':
    default:
      return bodypart;
  }
}

function conformNameToPartsOrder(bodyPart: string): string | keyof typeof partsOrder {
  // strips 'left ', 'right', and whitespace from bodypart string
  const fixedBodyPart = bodyPart.replace(/^(left |right |\s+ )| /g, '');

  return fixedBodyPart in partsOrder ? fixedBodyPart : bodyPart;
}

//used for the way legacy complaint names were made.
// DO NOT MODIFY FUNCTION BELOW.
export const createLegacyName = (parts: BodyParts) => {
  let partsInvolved = Object.values(parts || {}).reduce((arr: string[], sides) => {
    Object.entries(sides).forEach(([partName, partObj]) => {
      if (partObj.left || partObj.right) {
        const partChanged = partName.replace(/-/g, ' ');

        if (!arr.includes(partChanged)) {
          arr.push(partChanged);
        }
      }
    });

    return arr;
  }, []);

  const backParts = partsInvolved.filter(part => part.includes('back'));

  if (backParts?.length > 1) {
    partsInvolved = partsInvolved.filter(part => !part.includes('back'));
    partsInvolved.push('back');
  }

  return partsInvolved
    .sort((a, b) => {
      const aOrder = partsOrder[a] || 20;
      const bOrder = partsOrder[b] || 20;

      if (aOrder < bOrder) {
        return -1;
      }

      if (aOrder > bOrder) {
        return 1;
      }

      return 0;
    })
    .reduce(
      (acc, curr, i) =>
        `${acc}${i ? (partsInvolved.length === i + 1 ? ' and ' : ', ') : ''}${curr}`,
      ''
    );
};
